import { faDatabase } from '@fortawesome/pro-regular-svg-icons';
import { faExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useRef, useState } from 'react';
import VoxelIconButton from '../../../../components/voxel/Buttons/VoxelIconButton';
import { VoxelPopover } from '../../../../components/voxel/Popover/VoxelPopover';
import { VoxelPopoverContent } from '../../../../components/voxel/Popover/VoxelPopoverContent';
import { VoxelPopoverTrigger } from '../../../../components/voxel/Popover/VoxelPopoverTrigger';
import { GetProjectStatusResultGq } from '../../../../graphql/projects/queries/useGetProjectStatusGq';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';
import DeleteRequestDialog from './DeleteRequestDialog';
import DataProcessingInfo from './ProjectStatusDialog';

export interface StatusIndicatorProps {
  projectStatus?: GetProjectStatusResultGq;
  user: CurrentUserGq;
  projectId: number;
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  projectStatus,
  user,
  projectId,
}) => {
  // Delete request dialog
  const [deleteRequestId, setDeleteRequestId] = useState<number>(undefined);

  // External popover state, so that we can trigger it upon loading results change
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const firstRender = useRef(true);

  useEffect(() => {
    // Don't open if we don't have project status yet
    if (!projectStatus) return;

    // Don't open the popover on first render
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }

    if (projectStatus?.loading) {
      setPopoverOpen(true);
    }
  }, [projectStatus?.loading]);

  const blockedAspects: string[] = [];
  const team = user?.team;
  if (team?.blockMonthlyUseRankKw) blockedAspects.push('Rankings');
  if (team?.blockMonthlyProxies) blockedAspects.push('Proxy usage');
  if (team?.blockMonthlyUseSearchVolumeKw) blockedAspects.push('Search volume');
  if (team?.blockMonthlyUseChatGptDrafts) blockedAspects.push('ChatGPT');
  if (team?.blockMonthlyUseTranslateKw) blockedAspects.push('Keyword translations');

  const blocked = !!blockedAspects.length;

  return (
    <>
      <VoxelPopover enableArrow offsetPx={16} open={popoverOpen} onOpenChange={setPopoverOpen}>
        <VoxelPopoverTrigger enableActive>
          <VoxelIconButton
            icon={<FontAwesomeIcon icon={blocked ? faExclamation : faDatabase} size="lg" />}
            size="md"
            color={blocked ? 'error' : 'white'}
            textColorClass="opacity-70"
            hoverTextColorClass="hover:opacity-90"
            tooltip={popoverOpen ? undefined : 'Data processing status'}
            spinner={projectStatus?.loading}
          />
        </VoxelPopoverTrigger>
        <VoxelPopoverContent shadowClass="drop-shadow-lg">
          <DataProcessingInfo
            projectStatus={projectStatus}
            blockedAspects={blockedAspects}
            blocked={blocked}
            projectId={projectId}
            setDeleteRequestId={setDeleteRequestId}
          />
        </VoxelPopoverContent>
      </VoxelPopover>
      <DeleteRequestDialog
        projectId={projectId}
        requestId={deleteRequestId}
        setRequestId={setDeleteRequestId}
        openPopover={() => setPopoverOpen(true)}
      />
    </>
  );
};

export default StatusIndicator;
